import { useMutation, useQueryClient } from "@tanstack/react-query"
import { deleteJobEventAction } from "../../actions/deleteJobEvent.action"
import { useGlobalSnackbar } from "../../../../../context/SnackbarContext";

export const useDeleteJobEventAction = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useGlobalSnackbar();

    return useMutation({
        mutationFn: (eventUid: string) => deleteJobEventAction(eventUid),
        onMutate: async (eventUid) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['jobs'] });

            // Snapshot the previous value
            const previousJobsQueries = queryClient.getQueriesData<any>({ queryKey: ['jobs'] });

            // Optimistically update to the new value
            queryClient.setQueriesData<any>({ queryKey: ['jobs'] }, (old: any) => {
                if (!old) return old;

                const filterEvents = (events: any[]) => events.filter((e) => (e.uid || e.id) !== eventUid);

                // Handle direct array structure
                if (Array.isArray(old)) {
                    return old.map(job => ({
                        ...job,
                        events: job.events ? filterEvents(job.events) : job.events
                    }));
                }

                // Handle { data: { items: [] } } structure
                if (old.data?.items) {
                    return {
                        ...old,
                        data: {
                            ...old.data,
                            items: old.data.items.map((job: any) => ({
                                ...job,
                                events: job.events ? filterEvents(job.events) : job.events
                            }))
                        }
                    };
                }

                return old;
            });

            // Return a context object with the snapshotted value
            return { previousJobsQueries };
        },
        onError: (err: any, eventUid, context) => {
            // Rollback on error
            if (context?.previousJobsQueries) {
                context.previousJobsQueries.forEach(([queryKey, oldData]) => {
                    queryClient.setQueryData(queryKey, oldData);
                });
            }
            showSnackbar(err.message || "Failed to delete event", "error");
        },
        onSuccess: () => {
            showSnackbar("Event deleted successfully", "success");
        },
        onSettled: () => {
            // Always refetch after error or success to keep server in sync
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            queryClient.invalidateQueries({ queryKey: ['job-events'] });
            queryClient.invalidateQueries({ queryKey: ['job-events-no-pagination'] });
            queryClient.invalidateQueries({ queryKey: ['widget-job-events'] });
        }
    })
}
